/**
 * InsurerContactsSection — carnet de contacts d'un assureur (9.V.2b).
 *
 * Composition (skill vercel-composition-patterns) : sous-composants explicites
 * (ContactCard, ContactForm, EmailRow, EmailForm) plutôt que props booléennes ;
 * `ContactForm`/`EmailForm` servent création ET édition via `initial`+`onSubmit`.
 * Chaque contact affiche ses emails imbriqués. Statut via activate/deactivate,
 * jamais un toggle ambigu. Badges primaire/inactif = texte + couleur (jamais
 * couleur seule, a11y). Erreurs bilingues via getApiErrorMessage.
 */

import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  type InsurerContactEmailRow,
  type InsurerContactRow,
  useCreateInsurerContact,
  useCreateInsurerContactEmail,
  useDeactivateInsurerContact,
  useDeactivateInsurerContactEmail,
  useInsurerContactEmails,
  useInsurerContacts,
  useSetInsurerContactEmailStatus,
  useSetInsurerContactStatus,
  useUpdateInsurerContact,
  useUpdateInsurerContactEmail,
} from "../../../lib/queries/insurerContacts";

function Badge({ tone, children }: { tone: "primary" | "inactive"; children: React.ReactNode }) {
  const cls =
    tone === "primary"
      ? "bg-brand-50 text-brand-700 ring-brand-200"
      : "bg-slate-100 text-slate-600 ring-slate-200";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      {children}
    </span>
  );
}

/**
 * Raccourci « Principal » directement en liste — pas besoin d'ouvrir le
 * formulaire. Toggle : `onToggle(!isPrimary)`. La promotion (démotion
 * transactionnelle de l'ancien) est gérée serveur ; retirer le principal ne
 * promeut personne automatiquement. Accessible clavier (button natif) +
 * aria-pressed + aria-label explicite.
 */
function PrimaryStar({
  isPrimary,
  pending,
  onToggle,
  labelSet,
  labelRemove,
}: {
  isPrimary: boolean;
  pending: boolean;
  onToggle: (next: boolean) => void;
  labelSet: string;
  labelRemove: string;
}) {
  const label = isPrimary ? labelRemove : labelSet;
  return (
    <button
      type="button"
      aria-pressed={isPrimary}
      aria-label={label}
      title={label}
      disabled={pending}
      onClick={() => onToggle(!isPrimary)}
      className="focus-visible:ring-brand-500 rounded p-1 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 20 20"
        aria-hidden="true"
        fill={isPrimary ? "currentColor" : "none"}
        className={isPrimary ? "text-brand-500" : "hover:text-brand-400 text-slate-300"}
      >
        <path
          d="m10 2.6 2.25 4.56 5.03.73-3.64 3.55.86 5.01L10 14.6l-4.5 2.36.86-5.01L2.72 7.9l5.03-.73L10 2.6Z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/**
 * Dialog de désactivation d'un PRINCIPAL (contact ou email, 9.V.2b). Propose
 * un remplaçant parmi les éléments actifs, ou « continuer sans principal ».
 * Si aucun autre actif → avertissement + confirmation. Confirme via UN seul
 * appel (`onConfirm(replacementId | null)`) — aucune promotion auto silencieuse.
 */
function DeactivatePrimaryDialog({
  title,
  warning,
  options,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  title: string;
  warning: string;
  options: { id: string; label: string }[];
  pending: boolean;
  error: unknown;
  onConfirm: (replacementId: string | null) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const NONE = "__none__";
  const [choice, setChoice] = useState<string>(options.length > 0 ? options[0]!.id : NONE);
  const titleId = useId();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-40 grid place-items-center bg-slate-900/40 p-4"
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <h3 id={titleId} className="text-base font-semibold text-slate-900">
          {title}
        </h3>

        {options.length > 0 ? (
          <fieldset className="mt-3">
            <legend className="text-sm text-slate-600">
              {t("insurers.contacts.deactivate_primary.choose_replacement")}
            </legend>
            <div className="mt-2 space-y-1.5">
              {options.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="radio"
                    name="replacement"
                    value={o.id}
                    checked={choice === o.id}
                    onChange={() => setChoice(o.id)}
                    className="text-brand-600 focus:ring-brand-500 h-4 w-4 border-slate-300"
                  />
                  <span className="min-w-0 truncate">{o.label}</span>
                </label>
              ))}
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <input
                  type="radio"
                  name="replacement"
                  value={NONE}
                  checked={choice === NONE}
                  onChange={() => setChoice(NONE)}
                  className="text-brand-600 focus:ring-brand-500 h-4 w-4 border-slate-300"
                />
                <span>{t("insurers.contacts.deactivate_primary.continue_without")}</span>
              </label>
            </div>
          </fieldset>
        ) : (
          <p className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800">{warning}</p>
        )}

        <ErrorLine error={error} />

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="focus-visible:ring-brand-500 rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={() => onConfirm(choice === NONE ? null : choice)}
            className="btn-primary"
          >
            {pending ? t("app.loading") : t("insurers.contacts.deactivate_primary.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  if (!error) return null;
  return (
    <p role="alert" aria-live="polite" className="mt-1 text-sm text-red-700">
      {getApiErrorMessage(error, normalizeLang(language)) || t("errors.generic")}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Contact form (création + édition)
// ---------------------------------------------------------------------------

type ContactFormValues = {
  full_name: string;
  role: string;
  department: string;
  is_primary: boolean;
};

function ContactForm({
  initial,
  pending,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: ContactFormValues;
  pending: boolean;
  error: unknown;
  submitLabel: string;
  onSubmit: (v: ContactFormValues) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2"
    >
      <label className="block sm:col-span-2">
        <span className="label">{t("insurers.contacts.fields.full_name")}</span>
        <input
          className="input"
          value={form.full_name}
          autoComplete="off"
          onChange={(e) => setForm((s) => ({ ...s, full_name: e.target.value }))}
        />
        <span className="mt-1 block text-xs text-slate-500">
          {t("insurers.contacts.fields.full_name_hint")}
        </span>
      </label>
      <label className="block">
        <span className="label">{t("insurers.contacts.fields.role")}</span>
        <input
          className="input"
          value={form.role}
          onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))}
        />
      </label>
      <label className="block">
        <span className="label">{t("insurers.contacts.fields.department")}</span>
        <input
          className="input"
          value={form.department}
          onChange={(e) => setForm((s) => ({ ...s, department: e.target.value }))}
        />
      </label>
      <label className="flex items-center gap-2 sm:col-span-2">
        <input
          type="checkbox"
          checked={form.is_primary}
          onChange={(e) => setForm((s) => ({ ...s, is_primary: e.target.checked }))}
          className="text-brand-600 focus:ring-brand-500 h-4 w-4 rounded border-slate-300"
        />
        <span className="text-sm text-slate-700">{t("insurers.contacts.fields.is_primary")}</span>
      </label>
      <div className="sm:col-span-2">
        <ErrorLine error={error} />
        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="focus-visible:ring-brand-500 rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn-primary" disabled={pending}>
            {pending ? t("app.loading") : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Email form + row
// ---------------------------------------------------------------------------

type EmailFormValues = {
  email: string;
  label: string;
  is_primary: boolean;
  preferred_language: "" | "fr" | "en";
};

function EmailForm({
  initial,
  pending,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: EmailFormValues;
  pending: boolean;
  error: unknown;
  submitLabel: string;
  onSubmit: (v: EmailFormValues) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="mt-2 grid gap-3 rounded-lg border border-slate-200 bg-white p-3 sm:grid-cols-2"
    >
      <label className="block">
        <span className="label">{t("insurers.contacts.emails.fields.email")}</span>
        <input
          type="email"
          inputMode="email"
          spellCheck={false}
          autoComplete="off"
          className="input"
          value={form.email}
          onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
          required
        />
      </label>
      <label className="block">
        <span className="label">{t("insurers.contacts.emails.fields.label")}</span>
        <input
          className="input"
          value={form.label}
          placeholder={t("insurers.contacts.emails.fields.label_hint")}
          onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
        />
      </label>
      <label className="block">
        <span className="label">{t("insurers.contacts.emails.fields.preferred_language")}</span>
        <select
          className="input"
          value={form.preferred_language}
          onChange={(e) =>
            setForm((s) => ({
              ...s,
              preferred_language: e.target.value as EmailFormValues["preferred_language"],
            }))
          }
        >
          <option value="">—</option>
          <option value="fr">FR</option>
          <option value="en">EN</option>
        </select>
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={form.is_primary}
          onChange={(e) => setForm((s) => ({ ...s, is_primary: e.target.checked }))}
          className="text-brand-600 focus:ring-brand-500 h-4 w-4 rounded border-slate-300"
        />
        <span className="text-sm text-slate-700">
          {t("insurers.contacts.emails.fields.is_primary")}
        </span>
      </label>
      <div className="sm:col-span-2">
        <ErrorLine error={error} />
        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="focus-visible:ring-brand-500 rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn-primary" disabled={pending}>
            {pending ? t("app.loading") : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}

function EmailRow({
  insurerId,
  contactId,
  email,
  siblingActive,
}: {
  insurerId: string;
  contactId: string;
  email: InsurerContactEmailRow;
  siblingActive: InsurerContactEmailRow[];
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const update = useUpdateInsurerContactEmail(insurerId, contactId);
  const setStatus = useSetInsurerContactEmailStatus(insurerId, contactId);
  const deactivate = useDeactivateInsurerContactEmail(insurerId, contactId);
  const inactive = email.status === "inactive";

  const onDeactivateClick = () => {
    if (email.is_primary) {
      setConfirming(true); // principal → confirmation (remplaçant / sans / avertissement)
    } else {
      deactivate.mutate({ emailId: email.id }); // non-principal → direct
    }
  };

  if (editing) {
    return (
      <li className="py-2">
        <EmailForm
          initial={{
            email: email.email,
            label: email.label ?? "",
            is_primary: email.is_primary,
            preferred_language: email.preferred_language ?? "",
          }}
          pending={update.isPending}
          error={update.error}
          submitLabel={t("common.save")}
          onCancel={() => setEditing(false)}
          onSubmit={(v) =>
            update.mutate(
              {
                emailId: email.id,
                input: {
                  email: v.email.trim(),
                  label: v.label.trim() || null,
                  is_primary: v.is_primary,
                  preferred_language: v.preferred_language || null,
                },
              },
              { onSuccess: () => setEditing(false) },
            )
          }
        />
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2">
      <span className={`text-sm ${inactive ? "text-slate-400 line-through" : "text-slate-800"}`}>
        {email.email}
      </span>
      {email.label && <span className="text-xs text-slate-500">· {email.label}</span>}
      {email.preferred_language && (
        <span className="text-xs uppercase text-slate-400">{email.preferred_language}</span>
      )}
      {email.is_primary && (
        <Badge tone="primary">{t("insurers.contacts.emails.primary_badge")}</Badge>
      )}
      {inactive && <Badge tone="inactive">{t("insurers.contacts.emails.inactive_badge")}</Badge>}
      <span className="ml-auto flex items-center gap-2">
        {!inactive && (
          <PrimaryStar
            isPrimary={email.is_primary}
            pending={update.isPending}
            onToggle={(next) => update.mutate({ emailId: email.id, input: { is_primary: next } })}
            labelSet={t("insurers.contacts.emails.set_primary")}
            labelRemove={t("insurers.contacts.emails.remove_primary")}
          />
        )}
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="focus-visible:ring-brand-500 rounded px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2"
        >
          {t("insurers.contacts.edit")}
        </button>
        <button
          type="button"
          disabled={setStatus.isPending || deactivate.isPending}
          onClick={() =>
            inactive
              ? setStatus.mutate({ emailId: email.id, action: "activate" })
              : onDeactivateClick()
          }
          className="focus-visible:ring-brand-500 rounded px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2"
        >
          {inactive
            ? t("insurers.contacts.emails.activate")
            : t("insurers.contacts.emails.deactivate")}
        </button>
      </span>
      <span className="w-full">
        <ErrorLine error={setStatus.error || update.error || deactivate.error} />
      </span>
      {confirming && (
        <DeactivatePrimaryDialog
          title={t("insurers.contacts.deactivate_primary.title_email")}
          warning={t("insurers.contacts.deactivate_primary.warning_email")}
          options={siblingActive.map((e) => ({ id: e.id, label: e.email }))}
          pending={deactivate.isPending}
          error={deactivate.error}
          onCancel={() => setConfirming(false)}
          onConfirm={(replacementId) =>
            deactivate.mutate(
              { emailId: email.id, replacementEmailId: replacementId },
              { onSuccess: () => setConfirming(false) },
            )
          }
        />
      )}
    </li>
  );
}

function EmailList({ insurerId, contactId }: { insurerId: string; contactId: string }) {
  const { t } = useTranslation();
  const emails = useInsurerContactEmails(insurerId, contactId, "all");
  const create = useCreateInsurerContactEmail(insurerId, contactId);
  const [adding, setAdding] = useState(false);
  const items = emails.data?.items ?? [];

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("insurers.contacts.emails.title")}
        </h4>
        <button
          type="button"
          onClick={() => setAdding((a) => !a)}
          className="text-brand-700 hover:bg-brand-50 focus-visible:ring-brand-500 rounded-md px-2 py-0.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2"
        >
          {adding ? t("common.cancel") : `+ ${t("insurers.contacts.emails.add")}`}
        </button>
      </div>

      {adding && (
        <EmailForm
          initial={{ email: "", label: "", is_primary: false, preferred_language: "" }}
          pending={create.isPending}
          error={create.error}
          submitLabel={t("insurers.contacts.emails.add")}
          onCancel={() => setAdding(false)}
          onSubmit={(v) =>
            create.mutate(
              {
                email: v.email.trim(),
                label: v.label.trim() || null,
                is_primary: v.is_primary,
                preferred_language: v.preferred_language || null,
              },
              { onSuccess: () => setAdding(false) },
            )
          }
        />
      )}

      {items.length === 0 && !adding ? (
        <p className="py-2 text-sm text-slate-500">{t("insurers.contacts.emails.empty")}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((em) => (
            <EmailRow
              key={em.id}
              insurerId={insurerId}
              contactId={contactId}
              email={em}
              siblingActive={items.filter((o) => o.id !== em.id && o.status === "active")}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact card
// ---------------------------------------------------------------------------

function ContactCard({
  insurerId,
  contact,
  siblingActive,
}: {
  insurerId: string;
  contact: InsurerContactRow;
  siblingActive: InsurerContactRow[];
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const update = useUpdateInsurerContact(insurerId);
  const setStatus = useSetInsurerContactStatus(insurerId);
  const deactivate = useDeactivateInsurerContact(insurerId);
  const inactive = contact.status === "inactive";
  const name = contact.full_name?.trim() || t("insurers.contacts.service_generic");

  const onDeactivateClick = () => {
    if (contact.is_primary) {
      setConfirming(true); // principal → confirmation
    } else {
      deactivate.mutate({ contactId: contact.id }); // non-principal → direct
    }
  };

  return (
    <li className="rounded-lg border border-slate-200 p-4">
      <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
        <div className="min-w-0">
          <p className={`font-medium ${inactive ? "text-slate-400" : "text-slate-900"}`}>{name}</p>
          {(contact.role || contact.department) && (
            <p className="text-sm text-slate-500">
              {[contact.role, contact.department].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {contact.is_primary && (
            <Badge tone="primary">{t("insurers.contacts.primary_badge")}</Badge>
          )}
          {inactive && <Badge tone="inactive">{t("insurers.contacts.inactive_badge")}</Badge>}
        </div>
        <span className="ml-auto flex items-center gap-2">
          {!inactive && (
            <PrimaryStar
              isPrimary={contact.is_primary}
              pending={update.isPending}
              onToggle={(next) =>
                update.mutate({ contactId: contact.id, input: { is_primary: next } })
              }
              labelSet={t("insurers.contacts.set_primary")}
              labelRemove={t("insurers.contacts.remove_primary")}
            />
          )}
          <button
            type="button"
            onClick={() => setEditing((e) => !e)}
            className="focus-visible:ring-brand-500 rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2"
          >
            {editing ? t("common.cancel") : t("insurers.contacts.edit")}
          </button>
          <button
            type="button"
            disabled={setStatus.isPending || deactivate.isPending}
            onClick={() =>
              inactive
                ? setStatus.mutate({ contactId: contact.id, action: "activate" })
                : onDeactivateClick()
            }
            className="focus-visible:ring-brand-500 rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2"
          >
            {inactive ? t("insurers.contacts.activate") : t("insurers.contacts.deactivate")}
          </button>
        </span>
      </div>

      <ErrorLine error={setStatus.error || update.error || deactivate.error} />

      {confirming && (
        <DeactivatePrimaryDialog
          title={t("insurers.contacts.deactivate_primary.title_contact")}
          warning={t("insurers.contacts.deactivate_primary.warning_contact")}
          options={siblingActive.map((ct) => ({
            id: ct.id,
            label: ct.full_name?.trim() || t("insurers.contacts.service_generic"),
          }))}
          pending={deactivate.isPending}
          error={deactivate.error}
          onCancel={() => setConfirming(false)}
          onConfirm={(replacementId) =>
            deactivate.mutate(
              { contactId: contact.id, replacementContactId: replacementId },
              { onSuccess: () => setConfirming(false) },
            )
          }
        />
      )}

      {editing && (
        <div className="mt-3">
          <ContactForm
            initial={{
              full_name: contact.full_name ?? "",
              role: contact.role ?? "",
              department: contact.department ?? "",
              is_primary: contact.is_primary,
            }}
            pending={update.isPending}
            error={update.error}
            submitLabel={t("common.save")}
            onCancel={() => setEditing(false)}
            onSubmit={(v) =>
              update.mutate(
                {
                  contactId: contact.id,
                  input: {
                    full_name: v.full_name.trim() || null,
                    role: v.role.trim() || null,
                    department: v.department.trim() || null,
                    is_primary: v.is_primary,
                  },
                },
                { onSuccess: () => setEditing(false) },
              )
            }
          />
        </div>
      )}

      <EmailList insurerId={insurerId} contactId={contact.id} />
    </li>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function InsurerContactsSection({
  insurerId,
  insurerActive,
}: {
  insurerId: string;
  insurerActive: boolean;
}) {
  const { t } = useTranslation();
  const contacts = useInsurerContacts(insurerId, "all");
  const create = useCreateInsurerContact(insurerId);
  const [adding, setAdding] = useState(false);
  const items = contacts.data?.items ?? [];

  return (
    <section className="card p-5" aria-labelledby="insurer-contacts-heading">
      <div className="mb-4 flex items-center justify-between">
        <h2 id="insurer-contacts-heading" className="text-lg font-semibold text-slate-900">
          {t("insurers.contacts.title")}
        </h2>
        <button
          type="button"
          onClick={() => setAdding((a) => !a)}
          disabled={!insurerActive}
          title={!insurerActive ? t("insurers.contacts.empty") : undefined}
          className="text-brand-700 hover:bg-brand-50 focus-visible:ring-brand-500 rounded-md px-2.5 py-1 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {adding ? t("common.cancel") : `+ ${t("insurers.contacts.add")}`}
        </button>
      </div>

      {adding && (
        <div className="mb-4">
          <ContactForm
            initial={{ full_name: "", role: "", department: "", is_primary: false }}
            pending={create.isPending}
            error={create.error}
            submitLabel={t("insurers.contacts.add")}
            onCancel={() => setAdding(false)}
            onSubmit={(v) =>
              create.mutate(
                {
                  full_name: v.full_name.trim() || null,
                  role: v.role.trim() || null,
                  department: v.department.trim() || null,
                  is_primary: v.is_primary,
                },
                { onSuccess: () => setAdding(false) },
              )
            }
          />
        </div>
      )}

      {contacts.isLoading ? (
        <p className="py-4 text-sm text-slate-500">{t("app.loading")}</p>
      ) : contacts.isError ? (
        <ErrorLine error={contacts.error} />
      ) : items.length === 0 ? (
        <p className="py-4 text-sm text-slate-500">{t("insurers.contacts.empty")}</p>
      ) : (
        <ul className="space-y-3">
          {items.map((contact) => (
            <ContactCard
              key={contact.id}
              insurerId={insurerId}
              contact={contact}
              siblingActive={items.filter((o) => o.id !== contact.id && o.status === "active")}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
